import { useEffect, useRef, useState } from 'react';
import type { DossierCard, Track } from '../lib/types';
import { api } from '../lib/api';

export function Dossier({ track }: { track?: Track }) {
  const [cards, setCards] = useState<DossierCard[]>([]);
  const [state, setState] = useState<'idle' | 'loading' | 'done'>('idle');
  const [idx, setIdx] = useState(0);
  const scroller = useRef<HTMLDivElement>(null);
  const idxRef = useRef(0);
  idxRef.current = idx;

  const key = track ? track.videoId ?? `${track.title}|${track.artist}` : null;

  useEffect(() => {
    if (!track) {
      setCards([]);
      setState('idle');
      return;
    }
    let cancelled = false;
    setState('loading');
    setCards([]);
    setIdx(0);
    api
      .dossier(track.title, track.artist, track.videoId)
      .then((r) => {
        if (cancelled) return;
        setCards(r.cards ?? []);
        setState('done');
      })
      .catch(() => !cancelled && setState('done'));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const goTo = (n: number) => {
    setIdx(n);
    const s = scroller.current;
    if (!s) return;
    // scroll only the carousel itself — scrollIntoView would also yank every scrollable ancestor
    const smooth = !matchMedia('(prefers-reduced-motion: reduce)').matches;
    s.scrollTo({ left: n * s.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
  };

  useEffect(() => {
    if (cards.length < 2) return;
    const t = setInterval(() => goTo((idxRef.current + 1) % cards.length), 7000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards.length]);

  const onScroll = () => {
    const s = scroller.current;
    if (!s) return;
    const n = Math.round(s.scrollLeft / s.clientWidth);
    if (n !== idxRef.current) setIdx(n);
  };

  // optional feature: appear only when a story actually exists — no loading placeholder
  if (!track || state !== 'done' || cards.length === 0) return null;

  return (
    <section className="mt-6">
      <div className="mb-2 flex items-center justify-between text-xs tracking-[0.25em] text-muted">
        <span>LINER NOTES</span>
        {cards.length > 1 && (
          <span className="flex gap-1">
            {cards.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 w-1.5 rounded-full ${i === idx ? 'bg-air' : 'surface-2'}`}
              />
            ))}
          </span>
        )}
      </div>

      <div
        ref={scroller}
        onScroll={onScroll}
        className="flex snap-x snap-mandatory gap-3 overflow-x-auto"
      >
          {cards.map((c, i) => (
            <article key={i} className="w-full shrink-0 snap-start">
              {c.image && (
                <img
                  src={c.image}
                  alt=""
                  loading="lazy"
                  className="h-44 w-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              )}
              <div className="p-4">
                <div className="text-sm font-medium">{c.title}</div>
                <p className="mt-1 text-sm leading-relaxed text-muted">{c.body}</p>
                {c.source && (
                  <a
                    href={c.source}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-block text-[11px] text-air"
                  >
                    Wikipedia ↗
                  </a>
                )}
              </div>
            </article>
          ))}
      </div>
    </section>
  );
}
